import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Navigate,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";

import { AUTH_CALLBACK_PATH } from "../lib/auth-config.ts";

import { ChatThread } from "../chat/ChatThread.tsx";
import { alfredSearchSchema } from "../work/alfred/search.ts";
import { digitationSearchSchema } from "../work/digitacion/search.ts";
import { documentsSearchSchema } from "../work/documentos/search.ts";
import { UsagePanel } from "../work/UsagePanel.tsx";
import { AppShell, useWorkspace } from "./AppShell.tsx";
import { AuthCallback } from "./AuthCallback.tsx";
import { ChatShell } from "./ChatShell.tsx";
import { DashboardShell } from "./DashboardShell.tsx";
import { RequireSession } from "./RequireSession.tsx";
import type { SessionStore } from "./session.tsx";
import { SessionPending } from "./SessionScreens.tsx";
import { SESSION_TEXTS } from "./texts.ts";
import { CHAT_MODE_ENABLED } from "./workspace.ts";

/**
 * Las rutas de la aplicación.
 *
 * En código y no en archivos: son dos modos de trabajo (§9.2) con un puñado de
 * pantallas, y un generador que vigile una carpeta sería más maquinaria que
 * rutas.
 *
 * El árbol es el diseño: bajo la raíz --que solo pone el estado compartido--
 * cuelgan los DOS ARMAZONES, y cada pantalla nace dentro del suyo. Las
 * pantallas del dominio del programa cuelgan de `/work` cuando existan en el API.
 *
 * Lo que aporta el router aquí no es navegar --ya se navegaba-- sino que la
 * URL deje de manejarse a mano: un valor raro se valida en vez de caer en
 * silencio en la pantalla equivocada.
 *
 * Por encima de los armazones hay una capa más, la de la sesión: la raíz no
 * dibuja nada, `/auth/callback` cuelga de ella directamente --sin armazón,
 * porque el armazón arranca el chat y ahí aún no hay sesión--, y todo lo demás
 * cuelga de una ruta sin segmento que no deja pasar sin sesión.
 */
export interface RouterContext {
  readonly session: SessionStore;
}

/** `2026-06` es el mes; `2026`, el año entero. */
const PERIOD_PATTERN = /^\d{4}(-\d{2})?$/;

const searchSchema = z.object({
  /** La conversación abierta. Sobrevive a cambiar de modo, así que va aquí. */
  c: z.uuid().optional(),
  /**
   * El mes del panel. En la URL por la misma razón que la conversación:
   * recargar no lo pierde y el enlace que se comparte enseña lo mismo.
   *
   * El mes puede venir sin mes --`2026`-- y eso es un año entero; las
   * secciones que son mensuales lo dicen en vez de preguntar por un periodo
   * que no existe.
   */
  p: z
    .union([z.string(), z.number()])
    /* Un año entero viaja como `?p=2026`, y el router parsea lo que parece un
       número COMO un número. El periodo es texto --`2026-06` también lo es--,
       así que se devuelve a texto antes de mirarle la forma. Sin esto, elegir
       "Todo 2026" y recargar acaba en la pantalla de error del router. */
    .transform((value) => String(value))
    .refine(
      (value) => PERIOD_PATTERN.test(value),
      "El periodo se escribe AAAA-MM, o AAAA entero",
    )
    .optional(),
});

const rootRoute = createRootRouteWithContext<RouterContext>()({
  validateSearch: searchSchema,
  component: Outlet,
});

/** La vuelta del SSO (§10.3). Fuera del armazón. */
const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_CALLBACK_PATH,
  validateSearch: z.object({
    code: z.string().optional(),
    state: z.string().optional(),
    error: z.string().optional(),
  }),
  component: function CallbackRoute() {
    const params = callbackRoute.useSearch();
    const navigate = callbackRoute.useNavigate();
    return (
      <AuthCallback
        params={params}
        onDone={(returnTo) => {
          // `returnTo` es una ruta con su búsqueda (`/work?p=2026-06`), así
          // que va por el historial y no por `to`, que la tomaría por ruta.
          void navigate({ href: returnTo, replace: true });
        }}
      />
    );
  },
});

/**
 * Todo lo que necesita sesión. Sin segmento en la URL.
 *
 * `beforeLoad` recupera la sesión --o manda al SSO-- ANTES de que se monte
 * nada: el armazón arranca el chat, y el chat habla con el API. Lo que se
 * dibuja según cómo haya ido lo decide `RequireSession`, que es quien sabe
 * enseñar un fallo o un acceso denegado.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authenticated",
  beforeLoad: async ({ context, location }) => {
    await context.session.ensure(location.href);
  },
  /* Mientras se recupera la sesión se ve que se está entrando, desde el
     primer instante: sin esto el router no dibuja nada y solo queda el
     fondo --en producción, hasta diez minutos con el API sin contestar--. */
  pendingMs: 0,
  pendingMinMs: 0,
  pendingComponent: () => (
    <SessionPending>{SESSION_TEXTS.entering}</SessionPending>
  ),
  component: () => (
    <RequireSession>
      <AppShell>
        <Outlet />
      </AppShell>
    </RequireSession>
  ),
});

/**
 * El armazón conversacional. No añade segmento a la URL: el chat es la raíz,
 * que es donde se hacen las cosas.
 *
 * Con el modo Chat apagado (`CHAT_MODE_ENABLED`) la raíz lleva a Work antes de
 * montar nada, conservando la conversación y el mes: un enlace viejo a una
 * conversación, o el de vuelta del SSO, no se queda en una pantalla apagada.
 */
const chatShellRoute = createRoute({
  getParentRoute: () => appRoute,
  id: "chat-shell",
  beforeLoad: ({ search }) => {
    if (!CHAT_MODE_ENABLED) {
      redirect({
        to: "/work/documentos",
        search: { c: search.c, p: search.p },
        replace: true,
        throw: true,
      });
    }
  },
  component: ChatShell,
});

/**
 * `/chat` no es una dirección de la aplicación --el chat es la raíz--, pero es
 * la que uno escribe a mano: lleva a la raíz, y de ahí a donde toque.
 */
const chatAliasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chat",
  beforeLoad: ({ search }) => {
    redirect({
      to: "/",
      search: { c: search.c, p: search.p },
      replace: true,
      throw: true,
    });
  },
});

const chatRoute = createRoute({
  getParentRoute: () => chatShellRoute,
  path: "/",
  component: function ChatRoute() {
    const { chat, focusedMessage, clearFocusedMessage } = useWorkspace();
    return (
      <ChatThread
        chat={chat}
        density="full"
        focusMessageId={focusedMessage}
        onFocusHandled={clearFocusedMessage}
      />
    );
  },
});

/** El armazón del panel. Todo lo suyo cuelga de `/work`. */
const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/work",
  component: DashboardShell,
});

/**
 * La raíz de Work no tiene pantalla propia: lleva a la maestra de documentos,
 * conservando el periodo y la conversación de la URL.
 */
const workIndexRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "/",
  component: () => (
    <Navigate
      to="/work/documentos"
      search={(previous) => ({ c: previous.c, p: previous.p })}
      replace
    />
  ),
});

/** Lo que se ha gastado en modelos y cuánto cuesta, estimado (2.4). */
const usageRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "consumo",
  component: UsagePanel,
});

/**
 * El proceso de revisión era una página aparte: ahora es una vista de la
 * maestra. El enlace viejo sigue sirviendo, y no deja rastro en el historial.
 */
const reviewFlowRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "proceso-revision",
  component: () => (
    <Navigate
      to="/work/documentos"
      search={(previous) => ({
        c: previous.c,
        p: previous.p,
        vista: "proceso" as const,
      })}
      replace
    />
  ),
});

/**
 * Documentos: la página maestra --el Resumen, la lista, el proceso y el
 * acierto-- (`work/documentos/`). La vista y los filtros de la lista viajan en
 * la URL (`search.ts`).
 */
const invoiceReviewsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "documentos",
  validateSearch: documentsSearchSchema,
  component: lazyRouteComponent(
    () => import("../work/documentos/DocumentsPage.tsx"),
    "DocumentsPage",
  ),
});

/**
 * Un documento: por su número (su seguimiento vigente) o por el id de un
 * seguimiento, que es como enlaza la lista.
 */
const invoiceReviewRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "documentos/$reference",
  /* La de la maestra: «Volver» regresa a la misma vista y filtros. */
  validateSearch: documentsSearchSchema,
  component: lazyRouteComponent(
    () => import("../work/invoice-reviews/InvoiceReviewDetail.tsx"),
    "InvoiceReviewDetail",
  ),
});

/**
 * Digitación: la cola de documentos y el área de trabajo de quien los lee.
 *
 * Es de Work --su dirección empieza por `/work` y el conmutador lo dice--,
 * pero cuelga de la sesión y no del armazón del panel: tiene el suyo, que
 * ocupa toda la ventana. Perezosa por lo mismo que las demás pantallas
 * pesadas.
 */
const dataEntryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/work/digitacion",
  /* El documento abierto, el buscador y los filtros: todo en la URL, para
     compartir una búsqueda con un link (`work/digitacion/search.ts`). */
  validateSearch: digitationSearchSchema,
  component: lazyRouteComponent(
    () => import("../work/digitacion/DataEntryScreen.tsx"),
    "DataEntryScreen",
  ),
});

/**
 * Alfred: los pedidos de quien entra --todos, si es su admin-- con el
 * filtro por estado en la URL (`work/alfred/`).
 */
const alfredRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "alfred",
  validateSearch: alfredSearchSchema,
  component: lazyRouteComponent(
    () => import("../work/alfred/AlfredList.tsx"),
    "AlfredList",
  ),
});

/**
 * Un pedido a Alfred. Su URL es estable: es la que lleva el correo que avisa
 * de un pedido nuevo o de un cambio. Valida la búsqueda de la lista para
 * que «Volver» regrese al mismo filtro.
 */
const alfredTicketRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "alfred/$id",
  validateSearch: alfredSearchSchema,
  component: lazyRouteComponent(
    () => import("../work/alfred/AlfredTicketPage.tsx"),
    "AlfredTicketPage",
  ),
});

/** «Acierto» también es ahora una vista de la maestra. */
const accuracyRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "acierto",
  component: () => (
    <Navigate
      to="/work/documentos"
      search={(previous) => ({
        c: previous.c,
        p: previous.p,
        vista: "acierto" as const,
      })}
      replace
    />
  ),
});

/**
 * El árbol, aparte del router, para que una prueba pueda montarlo sobre un
 * historial en memoria sin arrastrar el router de la aplicación.
 */
export const routeTree = rootRoute.addChildren([
  callbackRoute,
  appRoute.addChildren([
    chatShellRoute.addChildren([chatRoute]),
    chatAliasRoute,
    dataEntryRoute,
    dashboardRoute.addChildren([
      workIndexRoute,
      usageRoute,
      reviewFlowRoute,
      invoiceReviewsRoute,
      invoiceReviewRoute,
      accuracyRoute,
      alfredRoute,
      alfredTicketRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // La pone `App` al montar el `RouterProvider`: la sesión es de la
  // aplicación, no del módulo del router.
  context: { session: undefined as unknown as SessionStore },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
